import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import { useEffect, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { useToast } from '../../../../../context/ToastContext';
import {
  useCreateLocationMutation,
  useGetLocationQuery,
  useUpdateLocationMutation
} from '../../../../../services/endpoints/places/location';
import { getCurrentUser } from '../../../../../services/helper';
import {
  Address,
  convertContactImage,
  convertLocationImages,
  formatResponse,
  SFCLocation
} from '../../../../../types/place.types';
import { phoneRegExp } from '../../../../../utils/consts';
import {
  getErrorMessage,
  getObjectDifference
} from '../../../../../utils/utils';
import { FadeIn } from '../../../../animations/FadeIn';
import { LocationForm } from './LocationForm';
import { canEditLocation } from '../../../../../utils/rbac';
import { Forbidden } from '../../../../../components/error/Forbidden';


export interface FormValues {
  id?: number;
  title: string;
  business_entity_name?: string;
  phone_number: string;
  primary_city: string;
  manager_id?: { value: number; label: string };
  scheduling_email: string;
  invoicing_email: string;
  hotline?: string;
  tax_id_number?: string;
  is_active: boolean;
  addresses: Address[];
  latitude: number;
  longitude: number;
  zoom: number;

  location_information_facebook?: string;
  location_information_instagram?: string;
  location_information_who_we_are?: string;
  location_information_franchise_owner_first_name?: string;
  location_information_franchise_owner_last_name?: string;
  location_information_franchise_owner_email?: string;
  location_information_jobs_url?: string;

  consumer_fusion_business_id?: number | string | null;

  images?: File[];
  franchise_images?: File[];
  contact_image?: File;

  slug?: string;

  // Nationwide Map
  state?: string;
  timezone?: string;
  map_x?: number | null;
  map_y?: number | null;
  show_on_map?: boolean;
  map_pin_type?: 'franchise' | 'outlet';
  map_group_id?: number | null;
}

type Overwrite<T, NewT> = Omit<T, keyof NewT> & NewT;

export type CreateFormValues = Overwrite<
    FormValues,
    {
      manager_id: number | null;
      location_information: {
        facebook?: string;
        instagram?: string;
        who_we_are?: string;
        franchise_owner_first_name?: string;
        franchise_owner_last_name?: string;
        franchise_owner_email?: string;
        jobs_url?: string;
      };
    }
>;

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
  editing?: boolean;
}

export const RemittanceAddressSchema = Yup.object({
  street: Yup.string().required('Street is required'),
  unit: Yup.string().nullable().optional(),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  zip_code: Yup.string().length(5).required('Zip Code is required')
});

const formatFormValue = (formValue?: FormValues | SFCLocation): CreateFormValues => {
  return JSON.parse(JSON.stringify(formValue)) as CreateFormValues;
};

interface AddEditLocationProps {
  editing?: boolean;
}

export const AddEditLocation: React.FC<AddEditLocationProps> = ({ editing }) => {
  const currentUser = getCurrentUser();
  const params = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [createLocation] = useCreateLocationMutation();
  const [updateLocation] = useUpdateLocationMutation();
  const { data, error, isLoading } = useGetLocationQuery(Number(params.id!), {
    skip: !editing || !params.id,
    refetchOnMountOrArgChange: true
  });
  const [initialValues, setInitialValues] = useState<FormValues>({
    title: '',
    business_entity_name: '',
    phone_number: '',
    primary_city: '',
    // manager: {},
    scheduling_email: '',
    invoicing_email: '',
    hotline: '',
    tax_id_number: '',
    is_active: true,
    addresses: [],
    latitude: 0,
    longitude: 0,
    zoom: 0,
    location_information_facebook: '',
    location_information_instagram: '',
    location_information_who_we_are: '',
    location_information_franchise_owner_first_name: '',
    location_information_franchise_owner_last_name: '',
    location_information_jobs_url: '',
    location_information_franchise_owner_email: '',

    consumer_fusion_business_id: '',

    images: [],
    franchise_images: [],

    slug: '',

    // Nationwide Map
    state: '',
    timezone: '',
    map_x: null,
    map_y: null,
    show_on_map: false,
    map_pin_type: 'franchise',
    map_group_id: null,

  });

  const validationSchemas = [
    Yup.object({
      title: Yup.string().required('Title is required'),
      business_entity_name: Yup.string().optional(),
      phone_number: Yup.string()
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .matches(phoneRegExp, 'Phone number is not valid')
          .min(10, 'Phone number should have at least 10 digits')
          .optional(),
      primary_city: Yup.string().required('City is required'),
      scheduling_email: Yup.string().email().required('Scheduling Email is required'),
      invoicing_email: Yup.string()
          .email()
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .optional(),
      hotline: Yup.string()
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .matches(phoneRegExp, 'Chef Hotline is not valid')
          .min(10, 'Chef Hotline should have at least 10 digits')
          .optional(),
      tax_id_number: Yup.string().optional(),
      is_active: Yup.boolean().required().default(true),
      timezone: Yup.string().when('is_active', {
        is: true,
        then: (schema) => schema.required('Time zone is required for active locations'),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      addresses: Yup.array().of(RemittanceAddressSchema).optional(),
      latitude: Yup.number().required(),
      longitude: Yup.number().required(),
      zoom: Yup.number().required(),

      location_information_facebook: Yup.string()
          .url('Invalid URL')
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .optional(),

      location_information_instagram: Yup.string()
          .url('Invalid URL')
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .optional(),

      location_information_who_we_are: Yup.string()
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .max(3000, 'Who We Are description must be 3000 characters or less')
          .optional(),
      location_information_franchise_owner_first_name: Yup.string()
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .optional(),
      location_information_franchise_owner_last_name: Yup.string()
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .optional(),
      location_information_franchise_owner_email: Yup.string()
          .email()
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .optional(),
      location_information_jobs_url: Yup.string()
          .url('Invalid URL')
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .optional(),

      consumer_fusion_business_id: Yup.number()
          .typeError('Business ID must be a number')
          .integer('Business ID must be a whole number')
          .positive('Business ID must be a positive number')
          .nullable()
          .transform((value, originalValue) => (originalValue === '' ? null : value))
          .optional(),

      document_type: Yup.string().url().optional(),
      slug: Yup.string()
          .matches(/^[a-z0-9_]+$/, 'Only lowercase letters, numbers, and underscores allowed')
          .min(2, 'Slug must be at least 2 characters')
          .max(60, 'Slug must be 60 characters or less')
          .required('URL slug is required'),
    }),
    Yup.object({
      files: Yup.array().optional(),
      file_metadata: Yup.array()
    })
  ];

  useEffect(() => {
    if (editing && error && !isLoading) {
      addToast('Error while loading session', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, error, isLoading]);

  useEffect(() => {
    if (data && data.location) {
      const { location } = data;
      let values = {
        ...location,
        ...{
          manager_id: {
            value: location?.manager?.id ?? 0,
            label:
                location?.manager?.first_name || location?.manager?.last_name
                    ? `${location?.manager?.first_name} ${location?.manager?.last_name}`
                    : ''
          },
          location_information_facebook: location?.location_information?.facebook ?? '',
          location_information_instagram: location?.location_information?.instagram ?? '',
          location_information_franchise_owner_first_name:
          location?.location_information?.franchise_owner_first_name,
          location_information_franchise_owner_last_name:
          location?.location_information?.franchise_owner_last_name,
          location_information_franchise_owner_email:
          location?.location_information?.franchise_owner_email,
          location_information_jobs_url: location?.location_information?.jobs_url,
          location_information_who_we_are: location?.location_information?.who_we_are ?? ''
        },

        images: convertLocationImages(location?.location_images),
        franchise_images: convertLocationImages(location?.franchise_images),
        contact_image: location?.location_information
            ? convertContactImage(location?.location_information)
            : undefined,
        slug: location?.slug ?? '',

        consumer_fusion_business_id: location?.consumer_fusion_business_id ?? '',

        // Nationwide Map
        state: location?.state ?? '',
        timezone: location?.timezone ?? '',
        map_x: location?.map_x ?? null,
        map_y: location?.map_y ?? null,
        show_on_map: location?.show_on_map ?? false,
        map_pin_type: location?.map_pin_type ?? 'franchise',
        map_group_id: location?.map_group_id ?? null,
      };
      setInitialValues(values);
    }
  }, [data]);

  const handleAddLocation = async (formValue: FormValues) => {
    try {
      // Drop API-computed/read-only fields (e.g. virtual `label`) so Joi accepts addresses.
      const sanitizeAddresses = (addresses: any[] = []) =>
        addresses.map(({ id, street, unit, city, state, zip_code, destroy }) => ({
          id,
          street,
          unit,
          city,
          state,
          zip_code,
          ...(destroy != null ? { destroy } : {})
        }));

      let submission = formatFormValue(formValue);
      submission.addresses = sanitizeAddresses(submission.addresses);

      submission.manager_id = formValue.manager_id?.value || null;

      // Strip the flat location_information_* form keys; they exist on the
      // form for Formik convenience but the API expects a nested object.
      delete (submission as any).location_information_facebook;
      delete (submission as any).location_information_instagram;
      delete (submission as any).location_information_who_we_are;
      delete (submission as any).location_information_franchise_owner_first_name;
      delete (submission as any).location_information_franchise_owner_last_name;
      delete (submission as any).location_information_franchise_owner_email;
      delete (submission as any).location_information_jobs_url;

      // Always send these keys (including "") so cleared marketing fields overwrite stored values.
      const locationInformation = {
        facebook: formValue.location_information_facebook ?? '',
        instagram: formValue.location_information_instagram ?? '',
        franchise_owner_first_name: formValue.location_information_franchise_owner_first_name ?? '',
        franchise_owner_last_name: formValue.location_information_franchise_owner_last_name ?? '',
        franchise_owner_email: formValue.location_information_franchise_owner_email ?? '',
        jobs_url: formValue.location_information_jobs_url ?? '',
        who_we_are: formValue.location_information_who_we_are ?? ''
      };

      submission.location_information = locationInformation;

      try {
        if (editing && data?.location) {
          let formattedLocation = formatFormValue(formatResponse(data?.location));
          formattedLocation.addresses = sanitizeAddresses(formattedLocation.addresses);
          formattedLocation.location_information = locationInformation;

          if ('location_images' in submission) {
            delete submission.location_images;
          }

          let update = getObjectDifference(formattedLocation, submission);

          update.phone_number = formValue.phone_number;
          update.id = data?.location.id;
          update.location_information = locationInformation;
          update.title = formValue.title;
          update.primary_city = formValue.primary_city;
          update.manager_id = formValue.manager_id?.value || null;
          update.is_active = formValue.is_active;
          update.latitude = formValue.latitude;
          update.longitude = formValue.longitude;
          update.zoom = formValue.zoom;
          update.contact_image = formValue.contact_image;
          update.images = formValue.images;
          update.franchise_images = formValue.franchise_images;
          update.state = formValue.state;
          update.timezone = formValue.timezone;
          update.map_x = formValue.map_x;
          update.map_y = formValue.map_y;
          update.show_on_map = formValue.show_on_map;
          update.map_pin_type = formValue.map_pin_type;
          update.map_group_id = formValue.map_group_id;
          update.clear_map_group = formValue.map_group_id == null;
          // Always send it; '' clears it (the API stores blank as NULL).
          update.consumer_fusion_business_id = formValue.consumer_fusion_business_id ?? '';
          const res = await updateLocation(update as CreateFormValues).unwrap();
          if (res && res.location) {
            addToast(`Location updated succesfully`, 'success');
          }

          navigate(`/admin/places/locations/${data?.location.id}`);
        } else {
          const res = await createLocation(submission).unwrap();
          if (res && res.location) {
            navigate(`/admin/places/locations/${res.location.id}`);
            addToast('Location created succesfully', 'success');
          } else throw new Error('A problem happened while creating Location');
        }
      } catch (e) {
        addToast(getErrorMessage(e), 'error');
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  // RBAC: Only Super Admin, Supreme Admin can edit/create locations
  if (!canEditLocation(currentUser)) {
    return <Forbidden />;
  }

  return (
      <FadeIn className="p-lg-4">
        <Stack
            gap={3}
            className="py-lg-0 px-4 py-2"
        >
          <div>
            <BackButton
                text={`Back to location${editing ? ' details' : 's'}`}
                color="#2B4E64"
                url={
                  editing ? `/admin/places/locations/${data?.location.id}` : '/admin/places/locations'
                }
            />
            <h2
                className="d-none d-lg-block text-info fw-bold mb-0"
                style={{ fontSize: '2.25rem' }}
            >
              {editing ? 'Edit' : 'New'} Location
            </h2>
          </div>

          <div
              className="p-lg-5 p-3"
              style={{
                boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
                border: '1px solid #EBEBEB',
                borderRadius: 10
              }}
          >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchemas[0]}
                onSubmit={handleAddLocation}
                enableReinitialize
            >
              {({
                  submitForm,
                  isSubmitting,
                  touched,
                  errors,
                  values,
                  setFieldValue
                }: FormikProps<FormValues>) => (
                  <FormikForm className="text-start">
                    <div style={{ minHeight: '65vh' }}>
                      <LocationForm
                          errors={errors}
                          touched={touched}
                          values={values}
                          setFieldValue={setFieldValue}
                          editing={editing}
                      />
                    </div>

                    <Row className="justify-content-end mt-3">
                      <Col
                          xs={6}
                          lg={3}
                      >
                        <SimpleButton
                            disabled={isSubmitting}
                            type="button"
                            onClick={submitForm}
                            className="w-100"
                            variant="primary"
                        >
                          {editing ? 'Save' : 'Create'}
                        </SimpleButton>
                      </Col>
                    </Row>
                  </FormikForm>
              )}
            </Formik>
          </div>
        </Stack>
      </FadeIn>
  );
};